import { useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LifeBuoy, Plus } from 'lucide-react';
import { Button } from '../../shared/ui/Button';
import { Skeleton } from '../../shared/ui/Skeleton';
import { EmptyState } from '../../shared/ui/EmptyState';
import { useTickets } from './hooks/useTickets';
import { useSupportSettings } from './hooks/useSupportSettings';
import { OpenTicketModal } from './OpenTicketModal';
import { TicketListItem } from './components/TicketListItem';
import { STATUS_LABELS, isRemoved, isUnreadForMe } from './lib/support';
import { useAuth } from '../auth/useAuth';
import type { SupportTicketStatus } from './lib/types';
import styles from './SupportView.module.css';

const FILTERS: Array<{ value: SupportTicketStatus | ''; label: string }> = [
  { value: '', label: 'Todos' },
  { value: 'open', label: STATUS_LABELS.open },
  { value: 'in_progress', label: STATUS_LABELS.in_progress },
  { value: 'resolved', label: STATUS_LABELS.resolved },
  { value: 'closed', label: STATUS_LABELS.closed },
];

export function SupportView() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const { tickets: allTickets, loading, isManager } = useTickets();
  const { settings } = useSupportSettings();
  const tickets = useMemo(() => allTickets.filter((t) => !isRemoved(t)), [allTickets]);
  const [openModal, setOpenModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState<SupportTicketStatus | ''>('');

  const counts = useMemo(() => {
    const c = { all: tickets.length, open: 0, in_progress: 0, resolved: 0, closed: 0 };
    for (const t of tickets) c[t.status] += 1;
    return c;
  }, [tickets]);

  const filtered = useMemo(
    () => (statusFilter ? tickets.filter((t) => t.status === statusFilter) : tickets),
    [tickets, statusFilter],
  );

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerText}>
          <h1 className={styles.title}>{isManager ? 'Suporte' : 'Meus chamados'}</h1>
          <p className={styles.subtitle}>
            {isManager
              ? 'Chamados abertos pela equipe — responda e acompanhe o status.'
              : 'Acompanhe seus chamados e converse com o suporte.'}
          </p>
        </div>
        {settings.enabled ? (
          <Button onClick={() => setOpenModal(true)} leftIcon={<Plus size={16} />}>
            Abrir chamado
          </Button>
        ) : null}
      </header>

      {isManager ? (
        <div className={styles.filters} role="tablist" aria-label="Filtrar por status">
          {FILTERS.map((f) => {
            const count = f.value ? counts[f.value] : counts.all;
            const active = statusFilter === f.value;
            return (
              <button
                key={f.value || 'all'}
                type="button"
                role="tab"
                aria-selected={active}
                className={`${styles.filterChip} ${active ? styles.filterOn : ''}`}
                onClick={() => setStatusFilter(f.value)}
              >
                {f.label}
                <span className={styles.filterCount}>{count}</span>
              </button>
            );
          })}
        </div>
      ) : null}

      {!settings.enabled ? (
        <EmptyState
          icon={<LifeBuoy size={40} />}
          title="Suporte indisponível"
          description="O suporte não está disponível para esta clínica."
        />
      ) : null}

      {loading ? (
        <div className={styles.list}>
          <Skeleton count={5} height={72} />
        </div>
      ) : filtered.length === 0 ? (
        // suporte desligado já mostra "Suporte indisponível" acima: não empilhar um 2º aviso vazio (P108 #2)
        settings.enabled ? <EmptyState
          icon={<LifeBuoy size={40} />}
          title="Nenhum chamado"
          description={
            isManager ? 'Nenhum chamado nesse filtro.' : 'Você ainda não abriu chamados de suporte.'
          }
        /> : null
      ) : (
        <div className={styles.list}>
          {filtered.map((t) => (
            <TicketListItem
              key={t.id}
              ticket={t}
              unread={isUnreadForMe(t, currentUser?.uid ?? '', isManager)}
              showAuthor={isManager}
              onClick={() => navigate(`/support/${t.id}`)}
            />
          ))}
        </div>
      )}

      <OpenTicketModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        originPath={location.pathname}
      />
    </div>
  );
}
